/**
 * PlatformAccount — "Akun Saya" untuk SEMUA Platform Staff aktif (self-service).
 * Hanya menyentuh identitas akun (users.name/email/password) — bukan branding Dynicty maupun tenant.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, UserRound } from "lucide-react";
import PlatformLayout from "@/components/PlatformLayout";
import { Card } from "@/components/ui/card";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { ProfileTab } from "./account/ProfileTab";
import { SecurityTab } from "./account/SecurityTab";

const TABS = [
  { key: "profile", label: "Profile", Icon: UserRound },
  { key: "security", label: "Security", Icon: ShieldCheck },
];

export function PlatformAccountPanel({ user, updateUser, logout, initialTab = "profile" }) {
  const nav = useNavigate();
  const mustChange = !!user?.must_change_password;
  const [tab, setTab] = useState(mustChange ? "security" : initialTab);
  useEffect(() => { if (mustChange) setTab("security"); }, [mustChange]);

  const onPasswordChanged = async () => {
    await logout?.();
    nav("/platform/login", { replace: true });
  };

  return (
    <Card className="bg-white/5 border-white/10 p-5 rounded-2xl" data-testid="platform-account-card">
      <div className="inline-flex rounded-xl border border-white/10 bg-white/5 p-1 mb-6" role="tablist">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key}
            disabled={mustChange && key !== "security"}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-colors ${
              tab === key ? "dynicty-gradient-bg text-white" : "text-white/60 hover:text-white disabled:opacity-40"}`}
            data-testid={`account-tab-${key}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      {tab === "profile"
        ? <ProfileTab user={user} onUpdated={updateUser} />
        : <SecurityTab mustChange={mustChange} onChanged={onPasswordChanged} />}
    </Card>
  );
}

export default function PlatformAccount() {
  const { user, updateUser, logout } = usePlatformAuth();
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "security" ? "security" : "profile";
  return (
    <PlatformLayout title="Akun Saya">
      {user ? <PlatformAccountPanel user={user} updateUser={updateUser} logout={logout} initialTab={initialTab} /> : null}
    </PlatformLayout>
  );
}
